/** Light / dark theme: preference storage, resolution and application. Keep in sync with public/index.html. */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'ikompete-theme';
const PREFERENCES: ThemePreference[] = ['light', 'dark', 'system'];

function defaultStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function readPreference(storage: Storage | null = defaultStorage()): ThemePreference {
  try {
    const value = storage?.getItem(THEME_STORAGE_KEY);
    return PREFERENCES.includes(value as ThemePreference) ? (value as ThemePreference) : 'system';
  } catch {
    return 'system';
  }
}

export function savePreference(pref: ThemePreference, storage: Storage | null = defaultStorage()): void {
  try {
    storage?.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    // Private mode or blocked storage: the choice still applies for this page view.
  }
}

export function systemPrefersDark(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveTheme(pref: ThemePreference, systemDark: boolean): ResolvedTheme {
  if (pref === 'system') return systemDark ? 'dark' : 'light';
  return pref;
}

export function applyTheme(resolved: ResolvedTheme, root: HTMLElement = document.documentElement): void {
  root.dataset.theme = resolved;
  root.style.colorScheme = resolved;
}
